import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Button from '../../../components/Button'
import Loader from '../../../components/Loader'
import Toast from '../../../components/Toast/Toast'
import { handleOpenModal } from '../../../utils'
import { HUTRIT_PRIMARY_COLOR } from '../../../utils/constants'
import { PRIVATE_ROUTES } from '../../../Routes/routes'
import {
  deleteCourseQuestion,
  getCourseOne,
  getQuestionsByCourse,
} from '../../../services/Courses'
import { TAGS } from '../../../services/Tags'
import QuestionFormModal, {
  QUESTION_FORM_MODAL_ID,
} from './components/QuestionFormModal'

const StatusBadge = ({ active }) => (
  <span
    className="badge"
    style={{
      background: active ? '#dcfce7' : '#fee2e2',
      color: active ? '#166534' : '#991b1b',
    }}
  >
    {active ? 'Activa' : 'Inactiva'}
  </span>
)

// Módulo de preguntas para el examen de un curso (master).
const CourseQuestions = () => {
  const { id } = useParams()
  const courseId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const MySwal = withReactContent(Swal)
  const [selectedQuestion, setSelectedQuestion] = useState(null)

  const { data: course } = useQuery(
    [TAGS.course_one, courseId],
    () => getCourseOne(courseId),
    {
      enabled: Boolean(courseId),
      select: (data) => data?.res?.course || null,
    }
  )

  const { data: questions = [], isLoading } = useQuery(
    [TAGS.course_questions, courseId],
    () => getQuestionsByCourse(courseId),
    {
      enabled: Boolean(courseId),
      select: (data) => {
        const list = data?.res?.questions
        return Array.isArray(list) ? list : []
      },
    }
  )

  const { mutate: removeQuestion } = useMutation(deleteCourseQuestion, {
    onSuccess: ({ response, res }) => {
      if (!response?.ok) {
        Toast({
          icon: 'error',
          title: res?.msg || res?.error || 'No se pudo eliminar la pregunta',
        })
        return
      }
      queryClient.invalidateQueries([TAGS.course_questions, courseId])
      Toast({ title: 'Pregunta eliminada correctamente' })
    },
    onError: () =>
      Toast({ icon: 'error', title: 'No se pudo eliminar la pregunta' }),
  })

  const handleDelete = (question) => {
    MySwal.fire({
      title: <strong>¿Deseas eliminar esta pregunta?</strong>,
      text: 'Se eliminarán también sus opciones. Esta acción no se puede deshacer.',
      icon: 'warning',
      confirmButtonColor: HUTRIT_PRIMARY_COLOR,
      confirmButtonText: 'Sí, eliminar',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    }).then(({ isConfirmed }) => {
      if (isConfirmed) removeQuestion({ questionId: question.id })
    })
  }

  const openCreate = () => {
    setSelectedQuestion(null)
    handleOpenModal({ id: QUESTION_FORM_MODAL_ID })
  }

  const openEdit = (question) => {
    setSelectedQuestion(question)
    handleOpenModal({ id: QUESTION_FORM_MODAL_ID })
  }

  return (
    <DashboardWrapper
      showTitle
      title={course?.title ? `Preguntas: ${course.title}` : 'Preguntas del examen'}
    >
      <QuestionFormModal
        courseId={courseId}
        question={selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
      />

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-white border"
          onClick={() =>
            navigate(
              PRIVATE_ROUTES.master_course_detail.replace(':id', String(courseId))
            )
          }
        >
          ← Volver al curso
        </button>
        <Button onClick={openCreate}>Crear pregunta</Button>
      </div>

      <p className="text-on-bg-secondary mb-4">
        Estas preguntas conforman el examen que los talentos responden al
        completar el curso.
      </p>

      {isLoading ? (
        <Loader />
      ) : questions.length === 0 ? (
        <p className="text-on-bg-secondary mb-0">
          Este curso todavía no tiene preguntas. Crea la primera para armar el
          examen.
        </p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {questions.map((question, index) => {
            const options = Array.isArray(question.question_options_courses)
              ? question.question_options_courses
              : []

            return (
              <div key={question.id} className="p-3 border rounded">
                <div className="d-flex gap-3 align-items-start flex-wrap">
                  <div
                    className="d-flex align-items-center justify-content-center fw-bolder text-white flex-shrink-0"
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #01a76a, #00cf83)',
                    }}
                  >
                    {index + 1}
                  </div>

                  <div className="flex-grow-1" style={{ minWidth: 200 }}>
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <p className="fw-bold text-on-bg-primary mb-0">
                        {question.question}
                      </p>
                      <StatusBadge active={question.active !== false} />
                    </div>

                    <ul className="list-unstyled mb-0">
                      {options.map((opt) => (
                        <li
                          key={opt.id}
                          className="text-on-bg-secondary d-flex align-items-center gap-2"
                          style={{
                            fontSize: 14,
                            opacity: opt.active === false ? 0.5 : 1,
                          }}
                        >
                          <span
                            style={{
                              color: opt.correct ? '#01a76a' : undefined,
                              fontWeight: opt.correct ? 700 : 400,
                            }}
                          >
                            {opt.correct ? '✓' : '•'} {opt.option}
                          </span>
                          {opt.active === false && (
                            <span className="badge bg-secondary">Inactiva</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      isSmall
                      onClick={() => openEdit(question)}
                    >
                      Editar
                    </Button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      style={{ borderRadius: '8px' }}
                      onClick={() => handleDelete(question)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </DashboardWrapper>
  )
}

export default CourseQuestions
